import { EventType } from "@/analytics/analytics-events";
import type { MeasurementModeProperties } from "@/analytics/measurement-mode-events-properties";
import { selectZoomLensActive } from "@/store/measurement-tool-selector";
import {
  PanoMeasurementToolState,
  setIsZoomLensActive,
  setPanoMeasurementToolState,
} from "@/store/measurement-tool-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { ToolName } from "@/store/ui/ui-slice";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  isIElementGenericPointCloudStream,
  type IElementGenericStream,
} from "@faro-lotv/ielement-types";
import { useEffect, useMemo } from "react";
import { useToolsAcquaintance } from "../tools/tools-acquaintance-context";

/**
 * Activates the zoom lens according to the current main object and
 * the current active tool in flywalk mode.
 *
 * @param overlay The current overlay object in flywalk mode
 * @param isAnimating whether an animation is being run
 * @param isPanoWithoutDepth whether the main object is a pano without depth information
 * @param isPanoPairedWithPickingPointCloud whether the main object is a pano paired with a point cloud for picking
 * @param isGaussianSplats whether the main object is a Gaussian splats dataset
 */
export function useActivateZoomLens(
  overlay: IElementGenericStream | null,
  isAnimating: boolean,
  isPanoWithoutDepth: boolean,
  isPanoPairedWithPickingPointCloud: boolean,
  isGaussianSplats: boolean,
): void {
  const zoomLensOn = useAppSelector(selectZoomLensActive);
  const dispatch = useAppDispatch();

  const { activeToolAndPicking, hasPointCloudBehind, setHasPointCloudBehind } =
    useToolsAcquaintance();

  // Gaussian splats cannot be picked, so like an odometry path pano without depth they rely on
  // the point cloud loaded as the overlay. When one is available and we are not animating, write
  // in the tool acquaintance context that the main object has a supporting point cloud, so that
  // more functions can be enabled, such as the zoom lens and 3D coordinates picking.
  const shouldHavePointCloudBehind = useMemo(
    () =>
      ((isPanoPairedWithPickingPointCloud && isPanoWithoutDepth) ||
        isGaussianSplats) &&
      !!overlay &&
      isIElementGenericPointCloudStream(overlay) &&
      !isAnimating,
    [
      isPanoPairedWithPickingPointCloud,
      isPanoWithoutDepth,
      isGaussianSplats,
      overlay,
      isAnimating,
    ],
  );

  // This hook determines whether the main object cannot be picked and has a point cloud behind.
  useEffect(() => {
    if (shouldHavePointCloudBehind !== hasPointCloudBehind) {
      setHasPointCloudBehind(shouldHavePointCloudBehind);
    }
  }, [shouldHavePointCloudBehind, hasPointCloudBehind, setHasPointCloudBehind]);

  // Zoom lens should be active active iff
  // 1. Main object is either a Gaussian splats dataset, or a pano image from an odometry
  //    path without depth information AND
  // 2. Overlay object is a point cloud stream AND
  // 3. Active tool is the measurement or the annotation tool
  // Here a useMemo is inserted so that the two useEffect hooks below can be triggered within the same re-render of the component
  // while triggering them in two distinct re-renders may cause the pano wizard to flicker in for an instant
  const shouldEnableZoomLens = useMemo(
    () =>
      shouldHavePointCloudBehind &&
      (activeToolAndPicking.activeTool === ToolName.measurement ||
        activeToolAndPicking.activeTool === ToolName.annotation),
    [shouldHavePointCloudBehind, activeToolAndPicking.activeTool],
  );

  // This hook determines whether the zoom lens should be active or not
  useEffect(() => {
    if (shouldEnableZoomLens !== zoomLensOn) {
      // Tracking when the zoom lens is enabled or disabled
      Analytics.track<MeasurementModeProperties>(
        shouldEnableZoomLens
          ? EventType.enableZoomLens
          : EventType.disableZoomLens,
        { context: isGaussianSplats ? "3D" : "360" },
      );
      dispatch(setIsZoomLensActive(shouldEnableZoomLens));
    }
  }, [zoomLensOn, dispatch, shouldEnableZoomLens, isGaussianSplats]);

  // This hook determines whether the pano measurement wizard should be activated
  useEffect(() => {
    const panoWizardOn =
      activeToolAndPicking.activeTool === ToolName.measurement &&
      isPanoWithoutDepth &&
      !shouldHavePointCloudBehind;
    dispatch(
      setPanoMeasurementToolState(
        panoWizardOn
          ? PanoMeasurementToolState.StartMeasure
          : PanoMeasurementToolState.None,
      ),
    );
  }, [
    activeToolAndPicking.activeTool,
    isPanoWithoutDepth,
    shouldHavePointCloudBehind,
    dispatch,
  ]);
}
